// Codeplug profile <-> .xlsx workbook (Admin > General > DMR / Analog codeplug programming).
// One sheet per entity (Profile, Contacts, RX Group Lists, Channels, Zones); every supported field
// round-trips. Import is strict, so nothing a user typed is silently dropped: another sheet is
// refused unless it is blank, an unknown column is refused if it holds any value, a duplicated
// column is refused, a Profile field other than Format/Name/Description (or one given twice) is
// refused, every value is type-checked, formulas are refused, and the result passes the client-side
// check before the server validates it again on save. The file size and every sheet's rows and
// columns are bounded before the cells are processed.
//
// Spreadsheet formula injection: user strings starting with = + - @ TAB CR (or the escape
// character ') are written with a leading apostrophe, and only such an escaped prefix is removed
// on import. All cells are written as values (never formulas).
import * as XLSX from 'xlsx';
import type { Codeplug, CodeplugChannel, ToneType } from '../../services/dmrCodeplug';
import { codeplugProblems, hzFromMhz, mhz } from './dmrCodeplugModel';

export const SHEETS = {
  profile: 'Profile',
  contacts: 'Contacts',
  rxGroupLists: 'RX Group Lists',
  channels: 'Channels',
  zones: 'Zones',
} as const;

export const FORMAT_MARKER = 'RadioOS DMR codeplug v1';

const CONTACT_HEADERS = ['Number', 'Name', 'Call Type', 'Call ID'];
const LIST_HEADERS = ['Number', 'Name', 'Contacts'];
const ZONE_HEADERS = ['Number', 'Name', 'Channels'];
const CHANNEL_HEADERS = [
  'Number', 'Name', 'Mode', 'RX Frequency (MHz)', 'TX Frequency (MHz)', 'TX Power', 'RX Only', 'TX Admit',
  'Color Code', 'Time Slot', 'TX Contact', 'RX Group List', 'DCDM',
  'Bandwidth', 'Squelch', 'RX Tone Type', 'RX Tone', 'TX Tone Type', 'TX Tone', 'Tail Elimination', 'Tail Phase',
];

const DANGEROUS = /^[=+\-@\t\r']/;
const ESCAPED = /^'[=+\-@\t\r']/;

/** Neutralizes a user string for a spreadsheet cell. */
export const escapeCell = (s: string): string => (DANGEROUS.test(s) ? `'${s}` : s);
/** Reverses escapeCell. */
export const unescapeCell = (s: string): string => (ESCAPED.test(s) ? s.slice(1) : s);

const yesNo = (b: boolean | undefined) => (b === undefined ? '' : b ? 'Yes' : 'No');
const list = (ns: number[]) => ns.join(',');

export interface WorkbookProfile {
  name: string;
  description: string;
  codeplug: Codeplug;
}

/** Builds the workbook for a profile. */
export const codeplugToWorkbook = (p: WorkbookProfile): XLSX.WorkBook => {
  const c = p.codeplug;
  const wb = XLSX.utils.book_new();
  const add = (name: string, rows: unknown[][]) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  };
  add(SHEETS.profile, [
    ['Field', 'Value'],
    ['Format', FORMAT_MARKER],
    ['Name', escapeCell(p.name)],
    ['Description', escapeCell(p.description)],
  ]);
  add(SHEETS.contacts, [CONTACT_HEADERS, ...c.contacts.map((x) => [x.number, escapeCell(x.name), x.call_type, x.call_id])]);
  add(SHEETS.rxGroupLists, [LIST_HEADERS, ...c.rx_group_lists.map((x) => [x.number, escapeCell(x.name), list(x.contacts)])]);
  add(SHEETS.channels, [
    CHANNEL_HEADERS,
    ...c.channels.map((ch) => [
      ch.number, escapeCell(ch.name), ch.mode, mhz(ch.rx_frequency_hz), mhz(ch.tx_frequency_hz), ch.tx_power, yesNo(ch.rx_only), ch.tx_admit,
      ch.color_code ?? '', ch.time_slot ?? '', ch.tx_contact ?? '', ch.rx_group_list ?? '', yesNo(ch.dcdm),
      ch.bandwidth ?? '', ch.squelch ?? '', ch.rx_tone_type ?? '', ch.rx_tone ?? '', ch.tx_tone_type ?? '', ch.tx_tone ?? '',
      yesNo(ch.tail_elimination), ch.tail_phase ?? '',
    ]),
  ]);
  add(SHEETS.zones, [ZONE_HEADERS, ...c.zones.map((x) => [x.number, escapeCell(x.name), list(x.channels)])]);
  return wb;
};

/** The workbook as .xlsx bytes. */
export const codeplugToXlsx = (p: WorkbookProfile): ArrayBuffer => XLSX.write(codeplugToWorkbook(p), { bookType: 'xlsx', type: 'array' });

export class WorkbookError extends Error {}

/** Largest accepted .xlsx file. A full 1024-channel profile exported here is under 1 MiB. */
export const MAX_WORKBOOK_BYTES = 8 * 1024 * 1024;
/** Rows read per sheet: header + 1024 channels, with room for blank rows. More is refused. */
export const MAX_SHEET_ROWS = 2_000;
/** Columns per sheet (the Channels sheet has 21). More is refused. */
export const MAX_SHEET_COLUMNS = 64;

const mib = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MiB`;

/** Refuses a file that is too large before any of it is read. */
export const checkWorkbookSize = (bytes: number) => {
  if (bytes > MAX_WORKBOOK_BYTES) {
    throw new WorkbookError(`The file is ${mib(bytes)}; a codeplug workbook may be at most ${mib(MAX_WORKBOOK_BYTES)}.`);
  }
};

const isBlankCell = (cell: XLSX.CellObject | undefined) =>
  !cell || (!cell.f && (cell.t === 'z' || cell.v === undefined || cell.v === null || cell.v === ''));

/** The sheet's full extent: with sheetRows the parsed !ref is cut, !fullref keeps the original. */
const checkSheetExtent = (ws: XLSX.WorkSheet, name: string) => {
  const ref = (ws as { '!fullref'?: string })['!fullref'] ?? ws['!ref'];
  if (!ref) return;
  const r = XLSX.utils.decode_range(ref);
  if (r.e.r + 1 > MAX_SHEET_ROWS) throw new WorkbookError(`Sheet "${name}" uses ${r.e.r + 1} rows; at most ${MAX_SHEET_ROWS} are accepted.`);
  if (r.e.c + 1 > MAX_SHEET_COLUMNS) throw new WorkbookError(`Sheet "${name}" uses ${r.e.c + 1} columns; at most ${MAX_SHEET_COLUMNS} are accepted.`);
};

type Row = Record<string, unknown>;

const readSheet = (wb: XLSX.WorkBook, name: string, headers: string[]): Array<{ row: Row; line: number }> => {
  const ws = wb.Sheets[name];
  if (!ws) throw new WorkbookError(`Sheet "${name}" is missing.`);
  checkSheetExtent(ws, name);
  for (const [addr, cell] of Object.entries(ws)) {
    if (!addr.startsWith('!') && (cell as XLSX.CellObject).f) {
      throw new WorkbookError(`Sheet "${name}" cell ${addr} contains a formula; formulas are not accepted.`);
    }
  }
  // Blank rows are kept here so reported row numbers are the spreadsheet's own; they are skipped below.
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '', blankrows: true });
  const firstRow = range.s.r + 1;
  const head = (aoa[0] ?? []).map((h) => String(h).trim());
  for (const h of headers) {
    if (!head.includes(h)) throw new WorkbookError(`Sheet "${name}" has no "${h}" column.`);
  }
  const duplicate = head.find((h, k) => h !== '' && head.indexOf(h) !== k);
  if (duplicate) throw new WorkbookError(`Sheet "${name}" has the column "${duplicate}" twice.`);
  const isEmpty = (v: unknown) => v === '' || v === undefined || v === null;
  const out: Array<{ row: Row; line: number }> = [];
  aoa.slice(1).forEach((cells, i) => {
    const line = firstRow + i + 1;
    if (cells.every(isEmpty)) return;
    head.forEach((h, k) => {
      if (!headers.includes(h) && !isEmpty(cells[k])) {
        throw new WorkbookError(`Sheet "${name}" row ${line}: column "${h || XLSX.utils.encode_col(range.s.c + k)}" is not a supported field; remove it.`);
      }
    });
    const row: Row = {};
    head.forEach((h, k) => headers.includes(h) && (row[h] = cells[k]));
    out.push({ row, line });
  });
  return out;
};

const at = (sheet: string, line: number, column: string) => `Sheet "${sheet}" row ${line}, ${column}`;

const text = (sheet: string, line: number, row: Row, column: string, required = true): string => {
  const v = row[column];
  if (v === undefined || v === null || v === '') {
    if (required) throw new WorkbookError(`${at(sheet, line, column)}: a value is required.`);
    return '';
  }
  return unescapeCell(String(v));
};

const int = (sheet: string, line: number, row: Row, column: string, required = true): number | undefined => {
  const v = row[column];
  if (v === undefined || v === null || v === '') {
    if (required) throw new WorkbookError(`${at(sheet, line, column)}: a number is required.`);
    return undefined;
  }
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  if (!Number.isInteger(n)) throw new WorkbookError(`${at(sheet, line, column)}: "${v}" is not a whole number.`);
  return n;
};

const choice = <T extends string>(sheet: string, line: number, row: Row, column: string, allowed: readonly T[], required = true): T | undefined => {
  const v = text(sheet, line, row, column, required).trim().toUpperCase();
  if (!v && !required) return undefined;
  if (!(allowed as readonly string[]).includes(v)) throw new WorkbookError(`${at(sheet, line, column)}: "${v}" is not one of ${allowed.join(', ')}.`);
  return v as T;
};

const bool = (sheet: string, line: number, row: Row, column: string, required = true): boolean | undefined => {
  const v = text(sheet, line, row, column, required).trim().toLowerCase();
  if (!v && !required) return undefined;
  if (['yes', 'true', '1'].includes(v)) return true;
  if (['no', 'false', '0'].includes(v)) return false;
  throw new WorkbookError(`${at(sheet, line, column)}: "${v}" must be Yes or No.`);
};

const numbers = (sheet: string, line: number, row: Row, column: string): number[] => {
  const v = text(sheet, line, row, column, false).trim();
  if (!v) return [];
  return v.split(/[,;\s]+/).filter(Boolean).map((part) => {
    const n = Number(part);
    if (!Number.isInteger(n) || n < 1) throw new WorkbookError(`${at(sheet, line, column)}: "${part}" is not a valid number.`);
    return n;
  });
};

const frequency = (sheet: string, line: number, row: Row, column: string): number => {
  const raw = text(sheet, line, row, column);
  const hz = hzFromMhz(raw);
  if (!hz) throw new WorkbookError(`${at(sheet, line, column)}: "${raw}" is not a frequency in MHz.`);
  return hz;
};

const expectNumber = (sheet: string, line: number, n: number, expected: number) => {
  if (n !== expected) throw new WorkbookError(`${at(sheet, line, 'Number')}: expected ${expected} (numbers are consecutive from 1, in row order).`);
};

const TONES: readonly ToneType[] = ['NONE', 'CTCSS', 'DCS', 'DCS_INVERTED'];

/** Parses a codeplug workbook; throws WorkbookError with the sheet/row/column of the first problem. */
export const workbookToCodeplug = (wb: XLSX.WorkBook): WorkbookProfile => {
  const known: readonly string[] = Object.values(SHEETS);
  for (const name of wb.SheetNames) {
    if (known.includes(name)) continue;
    const ws = wb.Sheets[name];
    const filled = ws && Object.keys(ws).find((addr) => !addr.startsWith('!') && !isBlankCell(ws[addr] as XLSX.CellObject));
    if (filled) {
      throw new WorkbookError(
        `Sheet "${name}" (cell ${filled}) is not part of the codeplug format (${known.join(', ')}); its data would be lost. Remove the sheet or move the data.`
      );
    }
  }

  const PROFILE_FIELDS = ['Format', 'Name', 'Description'];
  const info = new Map<string, unknown>();
  for (const { row, line } of readSheet(wb, SHEETS.profile, ['Field', 'Value'])) {
    const field = String(row['Field'] ?? '').trim();
    if (!PROFILE_FIELDS.includes(field)) {
      throw new WorkbookError(`Sheet "${SHEETS.profile}" row ${line}: "${field}" is not a profile field (${PROFILE_FIELDS.join(', ')}); remove the row.`);
    }
    if (info.has(field)) throw new WorkbookError(`Sheet "${SHEETS.profile}" row ${line}: the field "${field}" is given twice.`);
    info.set(field, row['Value']);
  }
  if (info.get('Format') !== FORMAT_MARKER) throw new WorkbookError(`Sheet "${SHEETS.profile}" does not identify a ${FORMAT_MARKER} workbook.`);
  const name = unescapeCell(String(info.get('Name') ?? '')).trim();
  const description = unescapeCell(String(info.get('Description') ?? '')).trim();

  const c: Codeplug = { schema_version: 1, contacts: [], rx_group_lists: [], channels: [], zones: [] };
  const S = SHEETS;
  readSheet(wb, S.contacts, CONTACT_HEADERS).forEach(({ row, line }, i) => {
    const number = int(S.contacts, line, row, 'Number')!;
    expectNumber(S.contacts, line, number, i + 1);
    c.contacts.push({
      number,
      name: text(S.contacts, line, row, 'Name').trim(),
      call_type: choice(S.contacts, line, row, 'Call Type', ['PRIVATE', 'GROUP', 'ALL'] as const)!,
      call_id: int(S.contacts, line, row, 'Call ID')!,
    });
  });
  readSheet(wb, S.rxGroupLists, LIST_HEADERS).forEach(({ row, line }, i) => {
    const number = int(S.rxGroupLists, line, row, 'Number')!;
    expectNumber(S.rxGroupLists, line, number, i + 1);
    c.rx_group_lists.push({ number, name: text(S.rxGroupLists, line, row, 'Name').trim(), contacts: numbers(S.rxGroupLists, line, row, 'Contacts') });
  });
  readSheet(wb, S.channels, CHANNEL_HEADERS).forEach(({ row, line }, i) => {
    const C = S.channels;
    const number = int(C, line, row, 'Number')!;
    expectNumber(C, line, number, i + 1);
    const mode = choice(C, line, row, 'Mode', ['DIGITAL', 'ANALOG'] as const)!;
    const ch: CodeplugChannel = {
      number,
      name: text(C, line, row, 'Name').trim(),
      mode,
      rx_frequency_hz: frequency(C, line, row, 'RX Frequency (MHz)'),
      tx_frequency_hz: frequency(C, line, row, 'TX Frequency (MHz)'),
      tx_power: choice(C, line, row, 'TX Power', ['LOW', 'HIGH'] as const)!,
      rx_only: bool(C, line, row, 'RX Only')!,
      tx_admit: choice(C, line, row, 'TX Admit', ['ALWAYS', 'CHANNEL_FREE'] as const)!,
    };
    const digitalCols = ['Color Code', 'Time Slot', 'TX Contact', 'RX Group List', 'DCDM'];
    const analogCols = ['Bandwidth', 'Squelch', 'RX Tone Type', 'RX Tone', 'TX Tone Type', 'TX Tone', 'Tail Elimination', 'Tail Phase'];
    const filled = (cols: string[]) => cols.filter((col) => row[col] !== '' && row[col] !== undefined);
    if (mode === 'DIGITAL') {
      const wrong = filled(analogCols);
      if (wrong.length) throw new WorkbookError(`${at(C, line, wrong[0])}: a DIGITAL channel must leave the analog columns empty.`);
      Object.assign(ch, {
        color_code: int(C, line, row, 'Color Code'),
        time_slot: int(C, line, row, 'Time Slot'),
        tx_contact: int(C, line, row, 'TX Contact'),
        rx_group_list: int(C, line, row, 'RX Group List'),
        dcdm: bool(C, line, row, 'DCDM'),
      });
    } else {
      const wrong = filled(digitalCols);
      if (wrong.length) throw new WorkbookError(`${at(C, line, wrong[0])}: an ANALOG channel must leave the digital columns empty.`);
      const rxType = choice(C, line, row, 'RX Tone Type', TONES)!;
      const txType = choice(C, line, row, 'TX Tone Type', TONES)!;
      Object.assign(ch, {
        bandwidth: choice(C, line, row, 'Bandwidth', ['NARROW', 'WIDE'] as const),
        squelch: int(C, line, row, 'Squelch'),
        rx_tone_type: rxType,
        tx_tone_type: txType,
        tail_elimination: bool(C, line, row, 'Tail Elimination'),
        tail_phase: int(C, line, row, 'Tail Phase'),
      });
      // Tone values keep their text form ("67.0", "023"); a numeric cell is formatted back.
      const tone = (col: string, type: ToneType) => {
        if (type === 'NONE') {
          if (filled([col]).length) throw new WorkbookError(`${at(C, line, col)}: must be empty when the tone type is NONE.`);
          return undefined;
        }
        const v = row[col];
        if (typeof v === 'number') return type === 'CTCSS' ? v.toFixed(1) : String(v).padStart(3, '0');
        return text(C, line, row, col).trim();
      };
      const rxTone = tone('RX Tone', rxType);
      const txTone = tone('TX Tone', txType);
      if (rxTone !== undefined) ch.rx_tone = rxTone;
      if (txTone !== undefined) ch.tx_tone = txTone;
    }
    c.channels.push(ch);
  });
  readSheet(wb, S.zones, ZONE_HEADERS).forEach(({ row, line }, i) => {
    const number = int(S.zones, line, row, 'Number')!;
    expectNumber(S.zones, line, number, i + 1);
    c.zones.push({ number, name: text(S.zones, line, row, 'Name').trim(), channels: numbers(S.zones, line, row, 'Channels') });
  });

  const problems = codeplugProblems(c);
  if (problems.length) throw new WorkbookError(`The workbook is not a valid codeplug: ${problems.slice(0, 5).join('; ')}${problems.length > 5 ? ` (+${problems.length - 5} more)` : ''}`);
  return { name, description, codeplug: c };
};

/**
 * Reads .xlsx bytes. Formulas are read (not evaluated) so they can be refused. At most
 * MAX_SHEET_ROWS rows per sheet are parsed; a longer sheet is refused, never cut.
 */
export const xlsxToCodeplug = (data: ArrayBuffer): WorkbookProfile => {
  checkWorkbookSize(data.byteLength);
  let wb: XLSX.WorkBook;
  try {
    wb = XLSX.read(data, { type: 'array', cellFormula: true, sheetRows: MAX_SHEET_ROWS });
  } catch {
    throw new WorkbookError('The file is not a readable .xlsx workbook.');
  }
  return workbookToCodeplug(wb);
};

/** Starts a browser download of .xlsx bytes. */
export const downloadXlsx = (bytes: ArrayBuffer, filename: string) => {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const codeplugExportFilename = (profileName: string, now = new Date()): string => {
  const safe = profileName.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'profile';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `codeplug-${safe}-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.xlsx`;
};
